import { useQuery } from "@tanstack/react-query";
import { fetchOrganization } from "@/api/OrganizationsAPI";
import { WorkingPointModel } from "@/api/Models";
import LoadingElement from "../Loading/LoadingElement";
import { useEffect, useState } from "react";
import { fetchWorkingPoint } from "@/api/WorkingPointAPI";
import { toast } from "../ui/use-toast";
import { getDecodedTokenDetails } from "../Token/TokenService";

function MyAccountInfo() {
    const [workingPointId, setWorkingPointId] = useState('');

    useEffect(() => {
        (async () => {
            const tokenDetails = await getDecodedTokenDetails();

            if (tokenDetails?.token) {
                try {
                    const workingPointUser = tokenDetails.WPIDClaim.toString();
                    setWorkingPointId(workingPointUser);
                } catch (error) {
                    console.error('Failed to decode token', error);
                }
            } else {
                console.error('No token found');
            }
        })();
    }, []);

    const { data: dataWP, isLoading: isLoadingWP, error: errorWP } = useQuery({
        queryKey: ["workingPoint", workingPointId],
        queryFn: fetchWorkingPoint,
        enabled: !!workingPointId,
    });

    const { data, isLoading, error } = useQuery({
        queryKey: ["organization", dataWP?.organizationId],
        queryFn: fetchOrganization,
        enabled: !!dataWP?.organizationId,
    });
    
    useEffect(() => {
        if (error || errorWP) {
            toast({ description: "Exista o eroare la preluarea informatiilor despre acest cont." });
        }
    }, [error, errorWP]);
    
    if (isLoading || isLoadingWP) return <p className="h-screen w-screen flex items-center justify-center"><LoadingElement /></p>;
    if (!data) return null;

    if (workingPointId === undefined) return <p>Nu exista date despre organizatie pe conturile de admin.</p>;
    
    return (
        <>
            <div className="mx-0 max-w-screen-xl px-4 py-8 sm:px-6 sm:py-12 lg:px-8">
                <div className="px-2 sm:px-0">
                    <h3 className="text-base font-semibold leading-7 text-gray-900">Profil</h3>
                    <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">Informatiile personale ale utilizatorului:</p>
                </div>
                <div className="mt-6 border-t border-gray-100">
                    <dl className="divide-y divide-gray-100">
                        <div className="px-2 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt className="text-sm font-medium leading-6 text-gray-900">Nume Organizatie</dt>
                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                {data?.name}
                            </dd>
                        </div>
                        <div className="px-2 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt className="text-sm font-medium leading-6 text-gray-900">C.U.I.</dt>
                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">{data?.id}</dd>
                        </div>
                        <div className="px-2 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt className="text-sm font-medium leading-6 text-gray-900">Persoana de contact</dt>
                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">{data?.contactPerson}</dd>
                        </div>
                        <div className="px-2 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt className="text-sm font-medium leading-6 text-gray-900">Telefon</dt>
                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                {data?.contactPhone}
                            </dd>

                        </div>
                        <div className="px-2 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                            <dt className="text-sm font-medium leading-6 text-gray-900">Email</dt>
                            <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                {data?.contactEmail}
                            </dd>
                        </div>
                        {data.isCreditLimitActive &&
                            <div className="px-2 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                                <dt className="text-sm font-medium leading-6 text-gray-900">Limita de credit</dt>
                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                    {data?.overdraftValue} RON
                                </dd>
                            </div>
                        }
                        {data.isCreditLimitActive &&
                            <div className="px-2 py-2 sm:grid sm:grid-cols-3 sm:gap-4 sm:px-0">
                                <dt className="text-sm font-medium leading-6 text-gray-900">Soldul disponibil</dt>
                                <dd className="mt-1 text-sm leading-6 text-gray-700 sm:col-span-2 sm:mt-0">
                                    <p className={data?.availableBalance < 0 ? "text-red-500" : "text-green-500"}>{data?.availableBalance} RON</p>
                                </dd>
                            </div>
                        }
                    </dl>
                </div>
            </div>

            <h3 className="text-base ml-8 mb-2 font-semibold leading-7 text-gray-900">Punctele mele de lucru</h3>


            <div className="relative ml-8 overflow-x-auto">
                <table className="w-full text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
                    <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                        <tr>
                            <th scope="col" className="px-6 py-3">
                                Nume
                            </th>
                            <th scope="col" className="px-6 py-3">
                                Email
                            </th>
                            <th scope="col" className="px-6 py-3">
                                Adresa
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data?.workingPoints.map((workingPoints: WorkingPointModel) => {
                            return (
                                <>
                                    <tr className="bg-white border-b dark:bg-gray-800 dark:border-gray-700">
                                        <th scope="row" className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white">
                                            {workingPoints.workingPointName}
                                        </th>
                                        <td className="px-6 py-4">
                                            {workingPoints.workingPointEmail}
                                        </td>
                                        <td className="px-6 py-4">
                                            {workingPoints.address.country}, {workingPoints.address.street} {workingPoints.address.number}, {workingPoints.address.postalCode}
                                        </td>
                                    </tr>
                                </>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
}

export default MyAccountInfo;